/* ── GroupCategoryModal ────────────────────────────────────────────────
   Modal dialog triggered by "View all ->" for any risk band or
   the non-privileged category. Displays all groups and accounts in that category
   with search, filtering, member inspection, and assignment actions.
   ──────────────────────────────────────────────────────────────────────── */

import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../../../components/ui/alert-dialog";
import { Checkbox } from "../../../components/ui/checkbox";
import { SearchInput } from "../../../components/SearchInput";
import { Users, User, ChevronDown, ChevronUp } from "lucide-react";
import type { UnifiedColumn, UnifiedGroup } from "../constants/pagAccountConfigConstants";
import {
  UNIFIED_COLUMNS,
  getUnifiedColumnMeta,
} from "../constants/pagAccountConfigConstants";
import { useSeverityColorSync } from "../../../../stores/severityColorStore";
import { cn } from "../../../components/ui/utils";

export interface GroupCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  column: UnifiedColumn | null;
  groups: UnifiedGroup[];
  editing: boolean;
  onMoveGroup: (ids: string[], targetColumn: UnifiedColumn) => void;
}

export const GroupCategoryModal = ({
  open,
  onOpenChange,
  column,
  groups,
  editing,
  onMoveGroup,
}: GroupCategoryModalProps) => {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (!val.trim()) {
      setAppliedSearch("");
    }
  };

  const handleSearchSubmit = (val: string) => {
    setAppliedSearch(val.trim());
  };
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [confirmMoveState, setConfirmMoveState] = useState<{
    ids: string[];
    targetCol: UnifiedColumn;
  } | null>(null);

  const severityColors = useSeverityColorSync();
  const meta = column ? getUnifiedColumnMeta(column, severityColors) : null;

  // Reset selection and state when dialog opens or column changes
  useEffect(() => {
    setSelectedIds([]);
    setSearch("");
    setAppliedSearch("");
    setExpandedGroupId(null);
    setConfirmMoveState(null);
  }, [open, column]);

  const filteredGroups = useMemo(() => {
    const term = appliedSearch.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter(
      (g) =>
        g.groupName.toLowerCase().includes(term) ||
        (g.userName ?? "").toLowerCase().includes(term) ||
        (g.displayName ?? "").toLowerCase().includes(term) ||
        (g.email ?? "").toLowerCase().includes(term) ||
        (g.description ?? "").toLowerCase().includes(term) ||
        (g.sourceSystemName ?? "").toLowerCase().includes(term) ||
        g.accounts?.some(
          (a) =>
            a.userName.toLowerCase().includes(term) ||
            (a.displayName ?? "").toLowerCase().includes(term) ||
            (a.email ?? "").toLowerCase().includes(term),
        ),
    );
  }, [groups, appliedSearch]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredGroups.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredGroups.map((g) => g.id));
    }
  };

  const requestMove = (ids: string[], targetCol: UnifiedColumn) => {
    if (column === "NON_PRIVILEGED" && targetCol === "IDENTIFIED") return;
    if (ids.length > 1) {
      setConfirmMoveState({ ids, targetCol });
    } else if (ids.length === 1) {
      onMoveGroup(ids, targetCol);
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  const handleConfirmMove = () => {
    if (confirmMoveState) {
      onMoveGroup(confirmMoveState.ids, confirmMoveState.targetCol);
      setSelectedIds((prev) =>
        prev.filter((id) => !confirmMoveState.ids.includes(id)),
      );
      setConfirmMoveState(null);
    }
  };

  if (!column || !meta) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-4xl sm:max-w-4xl max-h-[85vh] flex flex-col p-6">
          <DialogHeader className="pb-3 border-b border-border/80">
            <div className="flex items-center gap-3">
              <span
                className="size-3.5 rounded-full shrink-0"
                style={{ background: meta.color }}
              />
              <div>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <span>{meta.label}</span>
                  <span
                    className="rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums"
                    style={{ background: `${meta.color}20`, color: meta.color }}
                  >
                    {groups.length} {groups.length === 1 ? "item" : "items"}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {meta.description}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* ── Search & Bulk Action Bar ── */}
          <div className="my-3 flex flex-wrap items-center justify-between gap-3">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              placeholder="Search"
              size="md"
              className="w-72"
            />

            {editing && filteredGroups.length > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-border/70 bg-background/60 px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/40 transition-colors select-none">
                  <Checkbox
                    checked={
                      filteredGroups.length > 0 &&
                      selectedIds.length === filteredGroups.length
                    }
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all items"
                    className="size-3.5 rounded border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <span className="text-xs">
                    {selectedIds.length === filteredGroups.length && filteredGroups.length > 0
                      ? "Deselect All"
                      : `Select All (${filteredGroups.length})`}
                  </span>
                </label>

                {selectedIds.length > 0 && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 rounded-lg border border-blue-500/50 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-400 hover:bg-blue-500/20 transition-colors"
                        >
                          <span>Move {selectedIds.length} Selected</span>
                          <ChevronDown className="size-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Move to category
                        </div>
                        {UNIFIED_COLUMNS.filter((c) => c !== column && c !== "AVAILABLE" && !(column === "NON_PRIVILEGED" && c === "IDENTIFIED")).map((targetCol) => {
                          const targetMeta = getUnifiedColumnMeta(targetCol, severityColors);
                          return (
                            <DropdownMenuItem
                              key={targetCol}
                              onClick={() => requestMove(selectedIds, targetCol)}
                              className="flex items-center gap-2 cursor-pointer text-xs py-1.5"
                            >
                              <span
                                className="size-2 rounded-full shrink-0"
                                style={{ background: targetMeta.color }}
                              />
                              <span className="truncate">{targetMeta.label}</span>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <button
                      type="button"
                      onClick={() => setSelectedIds([])}
                      className="rounded-lg border border-border/70 bg-background/60 px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Groups & Accounts List ── */}
          <div className="flex-1 overflow-y-auto min-h-[300px] flex flex-col gap-2 pr-1">
            {filteredGroups.length === 0 ? (
              <div className="m-auto py-12 text-center text-xs text-muted-foreground">
                No Data Available
              </div>
            ) : (
              filteredGroups.map((group) => {
                const isAccount = group.itemType === "ACCOUNT";
                const hasMemberAccounts = !isAccount && (group.accounts?.length ?? 0) > 0;
                const isExpanded = expandedGroupId === group.id;
                const isSelected = selectedIds.includes(group.id);

                return (
                  <div
                    key={group.id}
                    className={cn(
                      "flex flex-col rounded-xl border p-3 transition-colors",
                      isSelected
                        ? "border-blue-500/80 bg-blue-500/10 ring-1 ring-blue-500/30"
                        : "border-border/60 bg-muted/20 hover:bg-muted/30",
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {editing && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelect(group.id)}
                            aria-label={`Select ${group.groupName}`}
                            className="size-4 shrink-0 rounded border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                        )}
                        <div
                          className={cn(
                            "size-8 rounded-full flex items-center justify-center shrink-0",
                            isAccount
                              ? "bg-indigo-500/15 text-indigo-400"
                              : "bg-blue-500/10 text-blue-400",
                          )}
                        >
                          {isAccount ? <User className="size-4" /> : <Users className="size-4" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-foreground truncate">
                              {group.groupName}
                            </p>
                            {isAccount && (
                              <span className="rounded bg-indigo-500/15 px-1.5 py-0.2 text-[9px] font-semibold text-indigo-400">
                                Account
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 pt-0.5 text-[11px] text-muted-foreground">
                            {isAccount ? (
                              <span>
                                {group.userName ? `@${group.userName}` : group.email || "Direct"}
                              </span>
                            ) : (
                              <span>
                                {group.accountCount}{" "}
                                {group.accountCount === 1 ? "account" : "accounts"}
                              </span>
                            )}
                            {group.sourceSystemName && (
                              <span>• {group.sourceSystemName}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* Show expand button if group has member accounts */}
                        {hasMemberAccounts && (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedGroupId((prev) => (prev === group.id ? null : group.id))
                            }
                            className="flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                            title={isExpanded ? "Hide member accounts" : "View member accounts"}
                          >
                            <span>{isExpanded ? "Hide" : "View"} accounts</span>
                            {isExpanded ? (
                              <ChevronUp className="size-3" />
                            ) : (
                              <ChevronDown className="size-3" />
                            )}
                          </button>
                        )}

                        {/* Move to dropdown menu for individual row (hidden when in multi-select mode) */}
                        {editing && selectedIds.length === 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-muted transition-colors shadow-xs"
                                title="Move to another category"
                              >
                                <span>Move to</span>
                                <ChevronDown className="size-3 text-muted-foreground" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                Move to category
                              </div>
                              {UNIFIED_COLUMNS.filter((c) => c !== column && c !== "AVAILABLE" && !(column === "NON_PRIVILEGED" && c === "IDENTIFIED")).map((targetCol) => {
                                const targetMeta = getUnifiedColumnMeta(targetCol, severityColors);
                                return (
                                  <DropdownMenuItem
                                    key={targetCol}
                                    onClick={() => {
                                      const idsToMove =
                                        selectedIds.includes(group.id) && selectedIds.length > 1
                                          ? selectedIds
                                          : [group.id];
                                      requestMove(idsToMove, targetCol);
                                    }}
                                    className="flex items-center gap-2 cursor-pointer text-xs py-1.5"
                                  >
                                    <span
                                      className="size-2 rounded-full shrink-0"
                                      style={{ background: targetMeta.color }}
                                    />
                                    <span className="truncate">{targetMeta.label}</span>
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>

                    {/* Expanded member accounts list */}
                    {isExpanded && hasMemberAccounts && (
                      <div className="mt-2.5 border-t border-border/50 pt-2 flex flex-col gap-1.5 pl-11">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Member Accounts ({group.accounts!.length})
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {group.accounts!.map((acc) => (
                            <div
                              key={acc.accountId}
                              className="flex items-center gap-2 rounded-lg border border-border/40 bg-card/60 p-1.5 text-[11px]"
                            >
                              <User className="size-3 text-muted-foreground shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-foreground">
                                  {acc.displayName || acc.userName}
                                </p>
                                <p className="truncate text-[10px] text-muted-foreground">
                                  {acc.email || `@${acc.userName}`}
                                </p>
                              </div>
                              {acc.riskLevel && (
                                <span className="rounded bg-red-500/15 px-1 py-0.2 text-[9px] font-semibold text-red-400">
                                  {acc.riskLevel}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Confirmation Dialog for Multiple Items ── */}
      <AlertDialog
        open={confirmMoveState !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setConfirmMoveState(null);
        }}
      >
        <AlertDialogContent className="z-[70]">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Move</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to move{" "}
              <strong className="text-foreground">
                {confirmMoveState?.ids.length}
              </strong>{" "}
              items to{" "}
              <strong className="text-foreground">
                {confirmMoveState
                  ? getUnifiedColumnMeta(confirmMoveState.targetCol, severityColors).label
                  : ""}
              </strong>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmMoveState(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              className="bg-blue-600 text-white hover:bg-blue-500"
            >
              Confirm Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
